// Legal Widget — observability (`/legal/:slug/observability`).
//
// Table of HostRegistryEntry rows (host, ip, surface, firstSeen, lastSeen,
// hitCount) from GET /api/v1/admin/niches/:slug/hosts, sorted by lastSeen desc.
// Empty state when no hits have been recorded. This is PASSIVE observability —
// it never resolves niche/brand, it only reports who is serving the content.
// Ported from the legal-widget dashboard's Observability screen.

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { legalApi, type HostRegistryEntry } from "../../lib/legalApi";
import {
  Card,
  EmptyState,
  PageHeader,
  Skeleton,
  tokens,
} from "../../lib/theme";

export default function LegalNicheObservability() {
  const params = useParams();
  const slug = params["slug"] ?? "";
  const [hosts, setHosts] = useState<HostRegistryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setHosts(null);
    legalApi
      .listHosts(slug)
      .then((rows) => {
        if (!cancelled) setHosts(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load");
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Sort by lastSeen desc (most recently active host first).
  const sorted = useMemo(() => {
    if (!hosts) return null;
    return [...hosts].sort((a, b) => b.lastSeen.localeCompare(a.lastSeen));
  }, [hosts]);

  return (
    <div>
      <PageHeader
        title={`Observability · ${slug}`}
        subtitle="Passive record of hosts / IPs serving this niche (never used to resolve brand)."
        actions={
          <Link to={`/legal/${encodeURIComponent(slug)}`}>← Back to editor</Link>
        }
      />

      {error && (
        <Card>
          <EmptyState title="Failed to load hosts" hint={error} />
        </Card>
      )}

      {!error && sorted === null && (
        <Card>
          <Skeleton height={48} />
          <div style={{ height: 12 }} />
          <Skeleton height={48} />
        </Card>
      )}

      {!error && sorted !== null && sorted.length === 0 && (
        <Card>
          <EmptyState
            title="No hosts yet"
            hint="No hosts have served this niche yet."
          />
        </Card>
      )}

      {!error && sorted !== null && sorted.length > 0 && (
        <Card padding={0}>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: tokens.font.size.base,
              }}
            >
              <thead>
                <tr>
                  <Th>URL</Th>
                  <Th>IP</Th>
                  <Th>First seen</Th>
                  <Th>Last seen</Th>
                  <Th align="right">Hits</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((h, idx) => (
                  <tr
                    key={h.url || h.host}
                    style={{
                      background:
                        idx % 2 === 0
                          ? "transparent"
                          : "rgba(255,255,255,0.015)",
                    }}
                  >
                    <Td mono wrap>
                      {h.url || h.host}
                    </Td>
                    <Td mono>{h.ip}</Td>
                    <Td>{h.firstSeen}</Td>
                    <Td>{h.lastSeen}</Td>
                    <Td align="right" mono>
                      {h.hitCount}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      style={{
        textAlign: align,
        padding: `${tokens.space.md}px ${tokens.space.lg}px`,
        borderBottom: `1px solid ${tokens.color.border}`,
        color: tokens.color.fg1,
        fontSize: tokens.font.size.xs,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  mono = false,
  wrap = false,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  mono?: boolean;
  /** Allow long values (URLs) to wrap/break instead of forcing nowrap. */
  wrap?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: `${tokens.space.md}px ${tokens.space.lg}px`,
        borderBottom: `1px solid ${tokens.color.border}`,
        color: tokens.color.fg0,
        whiteSpace: wrap ? "normal" : "nowrap",
        ...(wrap ? { wordBreak: "break-all", maxWidth: 480 } : {}),
        ...(mono
          ? {
              fontFamily: tokens.font.mono,
              fontVariantNumeric: "tabular-nums",
            }
          : {}),
      }}
    >
      {children}
    </td>
  );
}
